import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Spinner, PageSkeleton } from '@daycal/ui';
import { formatters } from '@daycal/shared';
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiSearch, FiCheck } from 'react-icons/fi';
import { api } from '../../lib/apiClient.js';

const PER_PAGE = 20;

// Short human label for the badge (falls back to a de-slugged action name).
const ACTION_LABEL = {
  'user.change_plan': 'Plan changed',
  'user.disable': 'User disabled',
  'user.enable': 'User enabled',
  'user.reset_password': 'Password reset',
  'user.delete': 'User deleted',
  'plan.create': 'Plan created',
  'plan.update': 'Plan updated',
  'promo.create_platform': 'Promo created',
  'promo.delete_platform': 'Promo deleted',
  'promo.disable_platform': 'Promo disabled',
  'promo.enable_platform': 'Promo enabled',
  'settings.update': 'Settings updated',
  'settings.payment_qr': 'Payment QR updated',
  'booking.send_email': 'Booking email sent',
  'legal_page.update': 'Legal page edited',
  'agency.create': 'Agency created',
  'agency.employee.create': 'Employee added',
  'agency.employee.update_email': 'Employee email changed',
  'agency.employee.suspend': 'Employee suspended',
  'agency.employee.reactivate': 'Employee reactivated',
  'agency.employee.remove': 'Employee removed',
  'agency.employee.self_reset_password': 'Employee set password',
};

function labelFor(action) {
  return ACTION_LABEL[action] || action.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// One plain-English sentence describing what happened, using the resolved names.
function explain(l) {
  const who = l.target_label || (l.target_type === 'user' ? 'a user' : l.target_type || 'a record');
  const promo = l.target_label ? `the "${l.target_label}" promo code` : 'a platform promo code';
  switch (l.action) {
    case 'user.change_plan': {
      const plan = l.meta_plan_name ? `the ${l.meta_plan_name} plan` : 'a new plan';
      const days = l.metadata?.validDays ? ` for ${l.metadata.validDays} days` : '';
      return `Moved ${who} to ${plan}${days}.`;
    }
    case 'user.disable': return `Disabled the account for ${who}.`;
    case 'user.enable': return `Re-enabled the account for ${who}.`;
    case 'user.reset_password': return `Reset the password for ${who}.`;
    case 'user.delete': return `Deleted and anonymized ${who}.`;
    case 'plan.create': return `Created the ${l.target_label || 'pricing'} plan.`;
    case 'plan.update': return `Updated the ${l.target_label || 'pricing'} plan.`;
    case 'promo.create_platform': return `Created ${promo}.`;
    case 'promo.delete_platform': return `Deleted ${promo}.`;
    case 'promo.disable_platform': return `Disabled ${promo}.`;
    case 'promo.enable_platform': return `Enabled ${promo}.`;
    case 'settings.update': return 'Updated platform settings (site / SMTP / payment config).';
    case 'settings.payment_qr': return 'Uploaded a new manual-payment QR image.';
    case 'booking.send_email': {
      const tpl = l.metadata?.template ? l.metadata.template.replace(/_/g, ' ') : 'booking';
      return `Manually sent a ${tpl} email for a booking.`;
    }
    case 'legal_page.update': return `Edited the “${l.target_id}” legal page.`;
    case 'agency.create': return 'Created a new agency workspace.';
    case 'agency.employee.create': return 'Added a new team employee.';
    case 'agency.employee.update_email': return 'Updated a team employee’s email.';
    case 'agency.employee.suspend': return 'Suspended a team employee.';
    case 'agency.employee.reactivate': return 'Reactivated a team employee.';
    case 'agency.employee.remove': return 'Removed a team employee.';
    case 'agency.employee.self_reset_password': return 'A team employee set a new password.';
    default: return `${labelFor(l.action)}.`;
  }
}

// Searchable action filter — a custom dropdown (native <select> can't host a search box).
function ActionFilter({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = options.filter((o) => o.toLowerCase().includes(search.trim().toLowerCase()));
  const choose = (v) => { onChange(v); setOpen(false); setSearch(''); };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-w-[13rem] items-center justify-between gap-2 rounded-dc-sm border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium shadow-2xs transition-colors hover:border-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        <span className={value ? 'font-mono text-xs text-slate-900' : 'text-slate-500'}>{value || 'All actions'}</span>
        <FiChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 z-30 mt-1.5 w-72 overflow-hidden rounded-dc border border-slate-200 bg-white shadow-lg shadow-slate-900/10">
          <div className="border-b border-slate-100 p-2">
            <div className="flex items-center gap-2 rounded-dc-sm bg-slate-50 px-2.5 py-1.5 ring-1 ring-transparent focus-within:bg-white focus-within:ring-brand-500">
              <FiSearch className="h-4 w-4 shrink-0 text-slate-400" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search actions…"
                className="w-full bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
              />
            </div>
          </div>
          <div className="custom-scrollbar max-h-72 overflow-y-auto py-1">
            <FilterOption label="All actions" active={value === ''} muted onClick={() => choose('')} />
            {filtered.map((o) => (
              <FilterOption key={o} label={o} active={value === o} onClick={() => choose(o)} />
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-slate-400">No actions match “{search}”.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterOption({ label, active, muted, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-50 ${
        active ? 'bg-brand-50/60' : ''
      }`}
    >
      <span
        className={`truncate ${muted ? 'text-sm' : 'font-mono text-xs'} ${
          active ? 'font-semibold text-brand-700' : muted ? 'text-slate-500' : 'text-slate-700'
        }`}
      >
        {label}
      </span>
      {active && <FiCheck className="h-4 w-4 shrink-0 text-brand-600" />}
    </button>
  );
}

// Audit log (architecture.md §21, §24 M10). Immutable record of every super-admin action.
// Server-side pagination: the API returns one page of rows plus the total count.
export default function AdminAudit() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState(''); // '' = all actions
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin-audit', page, action],
    queryFn: () =>
      api.adminListAuditLogs(
        `?page=${page}&per_page=${PER_PAGE}${action ? `&action=${encodeURIComponent(action)}` : ''}`,
      ),
    placeholderData: (prev) => prev, // keep the current page visible while the next loads
  });

  const logs = data?.audit_logs || [];
  const total = data?.total ?? 0;
  const actions = data?.actions || [];
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const onFilter = (value) => {
    setAction(value);
    setPage(1); // new filter → back to the first page
  };

  // If entries shrink (or on first load) keep the page within range.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (isLoading) return <PageSkeleton />;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Audit logs</h1>
        <div className="flex items-center gap-3">
          <ActionFilter value={action} options={actions} onChange={onFilter} />
          <span className="text-sm text-slate-400">{total} {action ? 'matching' : 'entries'}</span>
        </div>
      </div>

      <div className="grid gap-2">
        {logs.map((l) => (
          <Card key={l.id}>
            <div className="flex items-start justify-between gap-3 text-sm">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="neutral">{labelFor(l.action)}</Badge>
                  {l.target_label && <span className="font-semibold text-slate-800">{l.target_label}</span>}
                </div>
                <p className="mt-1.5 text-slate-600">{explain(l)}</p>
                <p
                  className="mt-1 font-mono text-[11px] text-slate-400"
                  title={`${l.target_type || ''}${l.target_id ? `:${l.target_id}` : ''}`}
                >
                  {l.action}
                  {l.target_type && <> · {l.target_type}:{l.target_id}</>}
                  {l.actor_label && <> · by {l.actor_label}</>}
                </p>
              </div>
              <span className="shrink-0 whitespace-nowrap text-slate-400">
                {formatters.formatDateTime(l.created_at, 'Asia/Kolkata')}
              </span>
            </div>
          </Card>
        ))}
        {logs.length === 0 && (
          <p className="text-slate-500">{action ? `No entries for "${action}".` : 'No audit entries yet.'}</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1.5 rounded-dc-sm border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FiChevronLeft className="h-4 w-4" /> Prev
          </button>

          <span className="flex items-center gap-2 text-sm text-slate-500">
            Page {page} of {totalPages} · {total} total
            {isFetching && <Spinner />}
          </span>

          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1.5 rounded-dc-sm border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next <FiChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
